import type { TextSegmentParser } from '../types';
import type {
  TextDirection,
  BidiInstance,
  ParsedWordSegmentWithDirection,
} from './types';
import { splitTextByLineBreaks } from './utils/text-splitting';
import { NON_BREAKING_SPACE, REGULAR_SPACE } from './utils/constants';
import { analyzeCharacters } from './word-parsing-phases/character-analysis';
import { segmentWords } from './word-parsing-phases/word-segmentation';
import { getWordDirectionality } from './word-parsing-phases/word-directionality';
import { resolveNeutralWords } from './word-parsing-phases/neutral-resolution';
import { groupWordsIntoRuns } from './word-parsing-phases/run-grouping';
import { reorderRuns } from './word-parsing-phases/run-reordering';
import { flattenRuns } from './word-parsing-phases/flattening';
import { applyCharacterMirroring } from './word-parsing-phases/character-mirroring';

/**
  Word-level bidirectional text parsing algorithm based on Unicode Bidirectional Algorithm (UBA).
  
  PURPOSE:
  Correctly parse and reorder text containing mixed LTR/RTL content for visual display,
  handling complex cases like Hebrew/Arabic text, character mirroring, and line breaks.
  
  COMPONENTS:
  - "bidi-js": Implements Unicode Bidirectional Algorithm for character-level analysis
  - "Intl.Segmenter": Provides correct word segmentation for international text
  
  WHY Intl.Segmenter (not str.split()):
  - Handles CJK (Chinese, Japanese, Korean) characters correctly
  - Supports complex emojis (👨‍👩‍👧‍👦) and emoji sequences with ZWJ
  - Processes ligatures (æ, œ) and accented characters (café, naïve)
  - Correctly segments text without spaces between words
  - Handles mixed languages and directions in one sentence
  
  ALGORITHM PHASES:
  
  1. TEXT PREPARATION:
     - Replace spaces with non-breaking spaces (\u00A0) to preserve line height of lines with only spaces
     - Split text by line breaks (\n, \r, \r\n) and process each line separately
  
  2. For each line, apply Unicode Bidirectional Algorithm:
  
     Phase 1: CHARACTER-LEVEL ANALYSIS
     - Apply bidi-js to get embedding levels (directionality) for each character
     - Determine paragraph direction from first paragraph level
  
     Phase 2: WORD SEGMENTATION  
     - Use Intl.Segmenter to split line into word segments
     - Mark each segment as word-like or non-word-like
  
     Phase 3: WORD DIRECTIONALITY ANALYSIS
     - Determine direction (ltr/rtl/neutral) for each word based on embedding levels
     - Assign embedding level to each word based on most dominant directionality
  
     Phase 4: NEUTRAL WORD RESOLUTION
     - Resolve neutral words (punctuation, numbers) based on surrounding words
  
     Phase 5: RUN GROUPING
     - Group consecutive words with same direction into directional runs
     - Each run has direction, embedding level, and word list
  
     Phase 6: RUN REORDERING
     - Apply reordering if content needs it:
       * Mixed content (both LTR and RTL words), OR
       * RTL paragraph context, OR  
       * Pure RTL content (Hebrew/Arabic text should be reordered regardless of paragraph direction)
     - Reverse words within RTL runs
     - Reorder runs based on embedding levels
  
     Phase 7: FLATTENING
     - Flatten reordered runs back to linear word sequence
  
     Phase 8: CHARACTER MIRRORING
     - Apply character mirroring (parentheses, brackets) only to RTL words
     - LTR words in RTL context are NOT mirrored
  
  3. RESULT:
     - Return array of lines, each containing reordered word segments
  
  EXAMPLE - Mixed LTR/RTL:
  Input: "Hello שלום עולם World"
  Phase 1: Embedding levels [2,2,2,2,2,1,1,1,1,1,1,1,1,1,1,1,2,2,2,2,2] (2=LTR in RTL context, 1=RTL)
  Phase 2: Words ["Hello", " ", "שלום", " ", "עולם", " ", "World"]  
  Phase 3: Directions [ltr, neutral, rtl, neutral, rtl, neutral, ltr]
  Phase 4: Resolved [ltr, rtl, rtl, rtl, rtl, rtl, ltr] (neutrals resolved based on RTL context)
  Phase 5: Runs [{ltr: ["Hello"]}, {rtl: [" ", "שלום", " ", "עולם", " "]}, {ltr: ["World"]}]
  Phase 6: Reordered [{ltr: ["World"]}, {rtl: [" ", "עולם", " ", "שלום", " "]}, {ltr: ["Hello"]}]
  Phase 7: Flattened ["World", " ", "עולם", " ", "שלום", " ", "Hello"]
  Result: "World עולם שלום Hello" (RTL context: entire sequence reversed, RTL words within run reversed)
  
*/

export const parseWord: TextSegmentParser = (
  str: string,
  textDirection: TextDirection,
  segmenter: Intl.Segmenter,
  bidi: BidiInstance,
) => {
  // Replace spaces with non-breaking spaces
  const preparedString = str.replaceAll(REGULAR_SPACE, NON_BREAKING_SPACE);

  const lines = splitTextByLineBreaks(preparedString);

  const segmentsBrokenByLines = lines.map(line => {
    // Handle empty lines (consecutive line breaks)
    if (line.length === 0) {
      return [];
    }
    return reorderLine(line, bidi, textDirection, segmenter);
  });

  return {
    segmentsBrokenByLines,
  };
};

const reorderLine = (
  text: string,
  bidi: BidiInstance,
  textDirection: TextDirection,
  segmenter: Intl.Segmenter,
): Array<ParsedWordSegmentWithDirection> => {
  // Phase 1: Apply Unicode Bidirectional Algorithm for character-level analysis
  const { embeddingLevels, paragraphDirection } = analyzeCharacters(
    text,
    textDirection,
    bidi,
  );

  // Phase 2: Segment words
  const words = segmentWords(segmenter, text);

  // Phase 3: Analyze word directionality
  const analyzedWords: Array<ParsedWordSegmentWithDirection> = words.map(
    (word, index) => ({
      ...word,
      direction: getWordDirectionality(word, embeddingLevels, word.start),
      embeddingLevel: embeddingLevels.levels[word.start],
      originalIndex: index,
    }),
  );

  // Phase 4: Resolve neutral words
  const resolvedWords = resolveNeutralWords(analyzedWords, paragraphDirection);

  // Phase 5: Group words into directional runs
  const runs = groupWordsIntoRuns(resolvedWords);

  // Phase 6: Reorder runs for visual presentation
  const visualRuns = reorderRuns(runs, paragraphDirection);

  // Phase 7: Flatten to get final visual word order
  const visualOrder = flattenRuns(visualRuns);

  // Phase 8: Apply character-level mirroring (parentheses, brackets, etc.)
  const mirroredVisualOrder = applyCharacterMirroring(
    visualOrder,
    bidi,
    textDirection,
  );

  return mirroredVisualOrder;
};
